import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import apiFunctions from "../../../../services/api-service";
import useToast from "../../../../components/common/useToast";
import { AxiosError } from "axios";
import { errorHandler } from "../../../../utils/HelperFunctions";

const BazarBhavView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();

    const [record, setRecord] = useState<any>(null);
    const [lastupdate, setLastUpdate] = useState<any>(null);
    const [sheets, setSheets] = useState<any[]>([]);
    const [goldRates, setGoldRates] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const getBazarBhavData = async (id: string) => {
        try {
            setLoading(true);
            const response = await apiFunctions.get(`/bazarbhav/data/${id}`);
            if (response && response.data && response.data.success) {
                setRecord(response.data.record);
                setLastUpdate(response.data.lastUpdated);
                setSheets(response.data.sheets || []);
                setGoldRates(response.data.goldRates || []);
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response.data.message,
                    life: 3000,
                });
            }
        } catch (err: any) {
            errorHandler(err as AxiosError);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            getBazarBhavData(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleBack = () => {
        navigate("/admin/bazarbhav");
    };

    return (
        <div className="p-4">
            <div className="flex items-center mb-4 cursor-pointer" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700 mr-2" />
                <h3 className="m-0">Back</h3>
            </div>

            <div className="mb-4">
                <h2>View Bazar Bhav</h2>
            </div>

            {/* Bazar Bhav Info Card */}
            <Card className="mb-6">
                {loading ? (
                    <div className="space-y-2">
                        <Skeleton width="50%" height="2rem" />
                        <Skeleton width="70%" height="1.5rem" />
                        <Skeleton width="30%" height="1.5rem" />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 w-full">
                        <div className="w-full">
                            <strong>Name:</strong> {record?.name}
                        </div>
                        <div className="w-full">
                            <strong>Link:</strong>{" "}
                            <a
                                href={record?.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline truncate block"
                            >
                                {record?.link}
                            </a>
                        </div>
                        {lastupdate && <div className="w-full">
                            <strong>Last Updated Rate (Gold and silver):</strong>{" "}{lastupdate}
                        </div>}
                    </div>
                )}
            </Card>

            {goldRates.length > 0 && (
                <Card className="mb-6">
                    <h3 className="mb-2 font-semibold">Gold & Silver Rates</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full table-auto border-collapse">
                            <thead className="bg-gray-200">
                                <tr>
                                    <th className="border p-2 text-center">Karat</th>
                                    <th className="border p-2 text-center">Purity</th>
                                    <th className="border p-2 text-center">Per Gram</th>
                                    <th className="border p-2 text-center">Per 10g</th>
                                </tr>
                            </thead>
                            <tbody>
                                {goldRates.map((rate, idx) => {
                                    const isGold = rate.karat.includes("K");
                                    const isSilver = rate.karat.includes("%");

                                    const rowBg = isGold
                                        ? "bg-yellow-50"     // light gold background
                                        : isSilver
                                            ? "bg-gray-100"      // light silver background
                                            : idx % 2 === 0
                                                ? "bg-gray-50"
                                                : "";

                                    const textColor = isGold
                                        ? "text-yellow-700 font-semibold"
                                        : isSilver
                                            ? "text-gray-700 font-semibold"
                                            : "";

                                    return (
                                        <tr key={idx} className={rowBg}>
                                            <td className={`border p-2 text-center ${textColor}`}>{rate.karat}</td>
                                            <td className={`border p-2 text-center ${textColor}`}>{rate.purity}</td>
                                            <td className={`border p-2 text-center ${textColor}`}>{rate.perGram}</td>
                                            <td className={`border p-2 text-center ${textColor}`}>{rate.per10g}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
            {/* Bazar Bhav Data Table */}
            <Card>
                <div className="overflow-x-auto">
                    {loading ? (
                        <Card>
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr>
                                            {[1, 2, 3].map(i => (
                                                <th key={i} className="p-2 border">
                                                    <Skeleton width="80%" />
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <tr key={i}>
                                                {[1, 2, 3].map(j => (
                                                    <td key={j} className="p-2 border">
                                                        <Skeleton width="90%" />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    ) : (
                        sheets.map((sheet, sheetIndex) => (
                            <Card key={sheetIndex} className="mb-6">
                                {/* SHEET TITLE */}
                                <h3 className="mb-4 font-semibold text-lg">
                                    {sheet.title}
                                </h3>

                                <div className="overflow-x-auto">
                                    <table className="w-full table-auto border-collapse">
                                        <thead>
                                            {sheet.data.slice(0, 6).map((row: string[], rowIdx: number) => (
                                                <tr key={rowIdx} className="bg-gray-200">
                                                    {row.map((cell: string, cellIdx: number) => (
                                                        <th key={cellIdx} className="border p-2 text-center" colSpan={3}>
                                                            {cell}
                                                        </th>
                                                    ))}
                                                </tr>
                                            ))}
                                        </thead>

                                        <tbody>
                                            {sheet.data.slice(6).map((row: string[], rowIdx: number) => (
                                                <tr
                                                    key={rowIdx}
                                                    className={rowIdx % 2 === 0 ? "bg-gray-50" : ""}
                                                >
                                                    {row.map((cell: string, cellIdx: number) => (
                                                        <td key={cellIdx} className="border p-2 text-center">
                                                            {cell}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        ))
                    )}
                </div>
            </Card>
        </div>
    );
};

export default BazarBhavView;
